import { useCallback, useMemo } from "react";
import { useMutation } from "@tanstack/react-query";
import {
  SpiritualFormChatSummaryCard,
  type SpiritualChatSummary,
} from "gsintegrations";
import { trackSpiritualProfileEvent } from "../analytics/spiritualProfile";
import { useCachedMedium } from "../hooks/useCachedMedium";
import { useCachedTerreiro } from "../hooks/useCachedTerreiro";
import { useFormChatSubmitSpiritualProfile } from "../hooks/useFormChatSubmitSpiritualProfile";
import { useProductAnalyticsBase } from "../hooks/useProductAnalyticsContext";
import {
  useSpiritualEntitiesQuery,
  useSpiritualLinesQuery,
} from "../hooks/useSpiritualProfile";
import type {
  FormChatMessageRequest,
  FormChatMessageResponse,
  FormChatState,
  SpiritualReferenceData,
} from "../repositories/formChat.repository";
import { formChatRepository } from "../repositories/formChat.repository";
import { extractAxiosApiError } from "../utils/extractAxiosApiError";
import { buildSpiritualProfileFormChatPayload } from "../utils/spiritualProfileFormChatPayloadBuilder";

const SPIRITUAL_EDITABLE_FIELDS: Array<{ key: string; label: string }> = [
  { key: "lineId", label: "Linha" },
  { key: "entryMode", label: "Tipo de registro" },
  { key: "entityId", label: "Entidade" },
  { key: "developmentStage", label: "Estágio" },
  { key: "visibility", label: "Visibilidade" },
  { key: "notes", label: "Observações" },
];

interface UseSpiritualFormChatOptions {
  sessionId?: string;
  formState: FormChatState;
  summary?: SpiritualChatSummary;
  onResponse: (response: FormChatMessageResponse) => void;
  onError?: (error: { code: string; message: string }) => void;
  onReset: () => void;
  onEdit?: () => void;
  onCloseAssistant?: () => void;
}

export function useSpiritualFormChatHelpers({
  sessionId,
  formState,
  summary,
  onResponse,
  onError,
  onReset,
  onEdit,
  onCloseAssistant,
}: UseSpiritualFormChatOptions) {
  const productBase = useProductAnalyticsBase();
  const { id: terreiroId } = useCachedTerreiro() ?? {};
  const { id: mediumId } = useCachedMedium() ?? {};
  const isPersonalProfile = !terreiroId;
  const { submitSpiritualProfileByChat } = useFormChatSubmitSpiritualProfile();
  const { data: linesData = [] } = useSpiritualLinesQuery();
  const { data: entitiesData = [] } = useSpiritualEntitiesQuery();

  const spiritualReferenceData = useMemo<SpiritualReferenceData>(() => {
    const selectedLineId =
      typeof formState.lineId === "string" ? formState.lineId : undefined;
    return {
      lines: linesData.map((line) => ({ id: line.id, name: line.name })),
      entities: entitiesData
        .filter((entity) => !selectedLineId || entity.lineId === selectedLineId)
        .map((entity) => ({
          id: entity.id,
          name: entity.name,
          lineId: entity.lineId,
        })),
    };
  }, [entitiesData, formState.lineId, linesData]);

  type SpiritualMutationInput = {
    payload: Omit<FormChatMessageRequest, "entityType">;
    referenceData: SpiritualReferenceData;
    isPersonalProfile: boolean;
  };

  const { mutateAsync: sendSpiritualMutation, isPending } = useMutation({
    mutationKey: ["form-chat", "spiritual-profile-message"],
    mutationFn: async ({
      payload,
      referenceData,
      isPersonalProfile: personalProfile,
    }: SpiritualMutationInput): Promise<FormChatMessageResponse> => {
      if (!terreiroId && !mediumId) {
        throw new Error("Owner scope required");
      }

      const fullPayload: FormChatMessageRequest = {
        entityType: "spiritual_profile",
        referenceData,
        isPersonalProfile: personalProfile,
        ...payload,
      };

      return terreiroId
        ? formChatRepository.sendMessage(terreiroId, fullPayload)
        : formChatRepository.sendPersonalMessage(mediumId!, fullPayload);
    },
    onError: () => {
      trackSpiritualProfileEvent("spiritual_profile_form_chat_submit_error", {
        terreiroId,
        mediumId,
        sourcePage: productBase.sourcePage,
      });
    },
  });

  const sendSpiritualRequest = useCallback(
    async (
      payload: Omit<FormChatMessageRequest, "entityType">,
    ): Promise<FormChatMessageResponse | undefined> => {
      if (!terreiroId && !mediumId) return undefined;

      try {
        const response = await sendSpiritualMutation({
          payload,
          referenceData: spiritualReferenceData,
          isPersonalProfile,
        });
        onResponse(response);
        return response;
      } catch (err: unknown) {
        const nextError = extractAxiosApiError(err);
        onError?.(nextError);
        throw err;
      }
    },
    [
      isPersonalProfile,
      mediumId,
      onError,
      onResponse,
      sendSpiritualMutation,
      spiritualReferenceData,
      terreiroId,
    ],
  );

  const handleConfirmSpiritualProfile = useCallback(async () => {
    try {
      if (!summary) {
        onError?.({
          code: "FORM_CHAT_INCOMPLETE",
          message:
            "Revise os dados do perfil espiritual no chat antes de confirmar.",
        });
        return;
      }

      let mergedState = formState;
      if (sessionId) {
        try {
          const response = await sendSpiritualRequest({
            sessionId,
            message: "",
            action: "confirm",
          });
          if (response?.formState) {
            mergedState = { ...mergedState, ...response.formState };
          }
        } catch {
          // Review is already visible — keep going with the client-side state.
        }
      }

      const payload = buildSpiritualProfileFormChatPayload(mergedState);
      if (!payload) {
        onError?.({
          code: "FORM_CHAT_INCOMPLETE",
          message: "Informe linha, estágio e visibilidade antes de adicionar.",
        });
        return;
      }

      await submitSpiritualProfileByChat({ data: mergedState });
      onReset();
      onCloseAssistant?.();
    } catch (err: unknown) {
      const nextError = extractAxiosApiError(
        err,
        "Não foi possível adicionar o registro. Revise os dados e tente novamente.",
      );
      onError?.(nextError);
    }
  }, [
    formState,
    onCloseAssistant,
    onError,
    onReset,
    sendSpiritualRequest,
    sessionId,
    submitSpiritualProfileByChat,
    summary,
  ]);

  const summaryCard = useMemo(() => {
    if (!summary) return null;
    return (
      <SpiritualFormChatSummaryCard
        summary={summary}
        confirmLabel="Adicionar registro"
        isLoading={isPending}
        onConfirm={handleConfirmSpiritualProfile}
        onEdit={onEdit}
      />
    );
  }, [handleConfirmSpiritualProfile, isPending, onEdit, summary]);

  return {
    spiritualReferenceData,
    sendSpiritualRequest,
    handleConfirmSpiritualProfile,
    summaryCard,
    editableFields: SPIRITUAL_EDITABLE_FIELDS,
    isPending,
  };
}
